import React from "react";

import { Button } from "reactstrap";
import Toggle from "react-toggle";
import {
  LuAlertCircle,
  LuBell,
  LuBuilding2,
  LuHistory,
  LuKeyRound,
  LuMessageSquare,
  LuPalette,
  LuQrCode,
  LuShieldCheck,
  LuUserCheck,
} from "react-icons/lu";

import AccountSettingRow from "./AccountSettingRow";
import AccountSettingSegmented from "./AccountSettingSegmented";
import AccountSettingsSection from "./AccountSettingsSection";
import EnterpriseQrCode from "../../../components/UI/EnterpriseQrCode";

import {
  ACCOUNT_SETTINGS_SECTION_BRANDING_AND_HISTORY,
  ACCOUNT_SETTINGS_SECTION_ENTERPRISE,
  ACCOUNT_SETTINGS_SECTION_SECURITY,
  NEEDS_ATTENTION_NOTIFY_ARIA_LABEL,
  NEEDS_ATTENTION_NOTIFY_LABEL,
  NEEDS_ATTENTION_NOTIFY_OPTIONS,
  NEEDS_ATTENTION_NOTIFY_POPOVER_BODY,
  NEEDS_ATTENTION_NOTIFY_POPOVER_HEADER,
  USER_CONTENT_WARNING_LABEL,
  USER_CONTENT_WARNING_POPOVER_BODY,
  USER_CONTENT_WARNING_POPOVER_HEADER,
  USER_UPLOAD_HISTORY_LABEL,
  USER_UPLOAD_HISTORY_LINK_LABEL,
  USER_UPLOAD_HISTORY_POPOVER_BODY,
} from "../../../utils/constants";
import {
  USER_VALIDATION_HEADING,
  USER_VALIDATION_OFF_DESC,
  USER_VALIDATION_OFF_LABEL,
  USER_VALIDATION_ON_DESC,
  USER_VALIDATION_ON_LABEL,
} from "../../../utils/registrationValidationStrings";

const EDIT_ENTERPRISE_INFORMATION_LABEL = "Edit Enterprise Information";
const ICON_SIZE = 16;

/**
 * The eleven shared Account Settings rows, grouped into the three Figma bands.
 * Every value, toggle state and handler is supplied by EnterpriseSettings.
 */
const AccountSettingsRows = ({
  orgId,
  isTrivvy,
  enterpriseName,
  industryName,
  whiteLabelSummary,
  smsRoutingPanel,
  onlyOAuth,
  mfaEnabled,
  userContentWarning,
  requireUserValidation,
  needsAttentionChannel,
  isSavingNeedsAttentionChannel,
  onEditEnterprise,
  onOpenSmsRouting,
  onOpenWhiteLabel,
  onGoToUploadHistory,
  onChangePlatformAccess,
  onToggleMFA,
  onToggleUserContentWarning,
  onChangeUserValidation,
  onChangeNeedsAttentionChannel,
}) => {
  let ssoPlatformName = "OurOffice";
  if (isTrivvy) {
    ssoPlatformName = "Trivvy";
  }

  return (
    <React.Fragment>
      <AccountSettingsSection title={ACCOUNT_SETTINGS_SECTION_ENTERPRISE}>
        <AccountSettingRow
          icon={<LuQrCode size={ICON_SIZE} />}
          label="Enterprise QR code"
          popoverTarget="enterprise-qr-code-popover"
          popoverHeader="Enterprise QR code"
          popoverBody="Share this QR code so employees can scan it and sign up with their phone number."
        >
          <EnterpriseQrCode orgId={orgId} />
        </AccountSettingRow>

        <AccountSettingRow
          icon={<LuUserCheck size={ICON_SIZE} />}
          label={USER_VALIDATION_HEADING}
        >
          <div
            className="bwai-as-radio-group"
            role="radiogroup"
            aria-label={USER_VALIDATION_HEADING}
          >
            <label className="bwai-as-radio">
              <input
                type="radio"
                name="requireUserValidation"
                checked={!requireUserValidation}
                onChange={() => onChangeUserValidation(false)}
              />
              <span className="bwai-as-radio-text">
                <span className="bwai-as-radio-title">{USER_VALIDATION_OFF_LABEL}</span>
                <span className="bwai-as-radio-desc">{USER_VALIDATION_OFF_DESC}</span>
              </span>
            </label>
            <label className="bwai-as-radio">
              <input
                type="radio"
                name="requireUserValidation"
                checked={!!requireUserValidation}
                onChange={() => onChangeUserValidation(true)}
              />
              <span className="bwai-as-radio-text">
                <span className="bwai-as-radio-title">{USER_VALIDATION_ON_LABEL}</span>
                <span className="bwai-as-radio-desc">{USER_VALIDATION_ON_DESC}</span>
              </span>
            </label>
          </div>
        </AccountSettingRow>

        <AccountSettingRow
          icon={<LuBuilding2 size={ICON_SIZE} />}
          label="Enterprise Name"
          popoverTarget="enterprise-name-popover"
          popoverHeader="Enterprise Name"
          popoverBody="Your enterprise name is set when the enterprise account is created. You can change it by clicking Edit Enterprise Information."
        >
          <div className="bwai-as-value bwai-as-value-ellipsis" title={enterpriseName}>
            {enterpriseName}
          </div>
          <span className="bwai-as-link" onClick={onEditEnterprise}>
            {EDIT_ENTERPRISE_INFORMATION_LABEL}
          </span>
        </AccountSettingRow>

        <AccountSettingRow
          icon={<LuBuilding2 size={ICON_SIZE} />}
          label="Enterprise Industry"
          popoverTarget="enterprise-industry-popover"
          popoverHeader="Enterprise Industry"
          popoverBody="Your enterprise industry is set when the enterprise account is created. You can change it by clicking Edit Enterprise Information."
        >
          <div className="bwai-as-value">{industryName}</div>
          <span className="bwai-as-link" onClick={onEditEnterprise}>
            {EDIT_ENTERPRISE_INFORMATION_LABEL}
          </span>
        </AccountSettingRow>

        <AccountSettingRow
          icon={<LuMessageSquare size={ICON_SIZE} />}
          label="User-Initiated SMS Chats"
          popoverTarget="sms-chats-popover"
          popoverHeader="User-Initiated SMS Chats"
          popoverBody="Chats can be initiated by any user via SMS text and consume SMS credits. Only the predefined recipient(s) for each chat will receive the user’s SMS and can respond on the platform."
        >
          <Button className="bwai-btn-secondary" onClick={onOpenSmsRouting}>
            <LuMessageSquare size={ICON_SIZE} />
            <span className="bwai-as-btn-label">Set up User-Initiated SMS Chats</span>
          </Button>
          {smsRoutingPanel}
        </AccountSettingRow>

        <AccountSettingRow
          icon={<LuBell size={ICON_SIZE} />}
          label={NEEDS_ATTENTION_NOTIFY_LABEL}
          popoverTarget="needs-attention-notification-popover"
          popoverHeader={NEEDS_ATTENTION_NOTIFY_POPOVER_HEADER}
          popoverBody={NEEDS_ATTENTION_NOTIFY_POPOVER_BODY}
        >
          <AccountSettingSegmented
            options={NEEDS_ATTENTION_NOTIFY_OPTIONS}
            value={needsAttentionChannel}
            onChange={onChangeNeedsAttentionChannel}
            disabled={isSavingNeedsAttentionChannel}
            ariaLabel={NEEDS_ATTENTION_NOTIFY_ARIA_LABEL}
          />
        </AccountSettingRow>
      </AccountSettingsSection>

      <AccountSettingsSection title={ACCOUNT_SETTINGS_SECTION_SECURITY}>
        <AccountSettingRow
          icon={<LuKeyRound size={ICON_SIZE} />}
          label={`Only allow Single Sign On (SSO) to ${ssoPlatformName} Platform`}
          popoverTarget="sso-platform-access"
          popoverHeader="Sign In/ Log in Platform Access"
          popoverBody="Only allow access to platform via SSO"
        >
          <Toggle
            checked={onlyOAuth}
            icons={true}
            className="moduleToggleSwitch"
            onChange={onChangePlatformAccess}
          />
        </AccountSettingRow>

        <AccountSettingRow
          icon={<LuShieldCheck size={ICON_SIZE} />}
          label="Enable Multi Factor Authentication(MFA)"
          popoverTarget="mfa-platform-access"
          popoverHeader="Multi-Factor Authentication (MFA)"
          popoverBody="When enabled, all Admins must use MFA to sign in for added security."
        >
          <Toggle
            checked={mfaEnabled}
            icons={true}
            className="moduleToggleSwitch"
            onChange={onToggleMFA}
          />
        </AccountSettingRow>

        <AccountSettingRow
          icon={<LuAlertCircle size={ICON_SIZE} />}
          label={USER_CONTENT_WARNING_LABEL}
          popoverTarget="user-content-warning-popover"
          popoverHeader={USER_CONTENT_WARNING_POPOVER_HEADER}
          popoverBody={USER_CONTENT_WARNING_POPOVER_BODY}
        >
          <Toggle
            checked={userContentWarning}
            icons={true}
            className="moduleToggleSwitch"
            onChange={onToggleUserContentWarning}
          />
        </AccountSettingRow>
      </AccountSettingsSection>

      <AccountSettingsSection
        title={ACCOUNT_SETTINGS_SECTION_BRANDING_AND_HISTORY}
      >
        <AccountSettingRow
          icon={<LuPalette size={ICON_SIZE} />}
          label="White label"
          popoverTarget="white-label-popover"
          popoverHeader="White label"
          popoverBody="Change the logo and the name to display on the top left corner and select a primary color."
        >
          <span className="bwai-as-link" onClick={onOpenWhiteLabel}>
            {whiteLabelSummary}
          </span>
        </AccountSettingRow>

        {isTrivvy && (
          <AccountSettingRow
            icon={<LuHistory size={ICON_SIZE} />}
            label={USER_UPLOAD_HISTORY_LABEL}
            popoverTarget="user-upload-history-popover"
            popoverHeader={USER_UPLOAD_HISTORY_LABEL}
            popoverBody={USER_UPLOAD_HISTORY_POPOVER_BODY}
          >
            <span className="bwai-as-link" onClick={onGoToUploadHistory}>
              {USER_UPLOAD_HISTORY_LINK_LABEL}
            </span>
          </AccountSettingRow>
        )}
      </AccountSettingsSection>
    </React.Fragment>
  );
};

export default AccountSettingsRows;
